import { ToastContainer } from '@montugroup/design-system';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale/en-GB';

import '@montugroup/circuit-shared/utils/polyfills';

import { PatientConsentProvider } from '@/context/PatientConsentProvider';
import { useSession, useSessionSync } from '@montugroup/circuit-shared';
import { CircularProgressWrapper } from '@montugroup/circuit-shared/components/common/CircularProgressWrapper';
import { BrazeContentCardContext } from '@montugroup/circuit-shared/context/braze/BrazeContentCardContext';
import { ErrorManagementProvider } from '@montugroup/circuit-shared/context/ErrorManagement';
import { PreferencesProvider } from '@montugroup/circuit-shared/context/Preferences';
import { useUserFeatureFlagsSync } from '@montugroup/circuit-shared/hooks/featureFlags/useUserFeatureFlagsSync';
import { useNavigateSetup } from '@montugroup/circuit-shared/hooks/useNavigateSetup';
import { useSetDatadogUser } from '@montugroup/circuit-shared/hooks/useSetDatadogUser';
import { useEffect } from 'react';
import AlertSnackbarProvider from './components/error/AlertSnackbarProvider';
import { PatientReferralProvider } from './context/PatientReferral';
import { useAxiosSetup } from './hooks/useAxiosSetup';
import { useBraze } from './hooks/useBraze';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { usePortalRedirector } from './hooks/usePortalRedirector';
import useTrackingProviders from './hooks/useTrackingProviders';
import Maintenance from './pages/maintenance/Maintenance';
import { AuthenticatedSnowplow } from './providers/authenticated-snowplow';
import Routes from './routes/routes';
import { Logger } from './utils/logger';

const logger = new Logger('App');

function App() {
  useNavigateSetup();
  useAxiosSetup();
  useSetDatadogUser();
  const shouldShowMaintenanceScreen = useFeatureFlag('show_maintenance_screen');
  const shouldUseSessionStorageForAuth = useFeatureFlag('auth_enable_session_storage');
  const isSessionSyncEnabled = useFeatureFlag('auth_enable_cross_tab_session_sync');
  const { storageType, setStorageType } = useSession();
  const { loading: loadingUserFlags } = useUserFeatureFlagsSync();
  const { brazeContentCards } = useBraze();
  const { isPendingRedirection } = usePortalRedirector();
  const { isPendingSessionSync } = useSessionSync({
    isFlagReady: !isPendingRedirection && !loadingUserFlags,
    isFlagEnabled: shouldUseSessionStorageForAuth && isSessionSyncEnabled,
  });
  useTrackingProviders({ ready: !loadingUserFlags });

  // TODO: remove useEffect after sessionStorage migration is done
  useEffect(() => {
    if (storageType !== 'sessionStorage' && shouldUseSessionStorageForAuth === true) {
      setStorageType('sessionStorage');
    } else if (storageType !== 'localStorage' && shouldUseSessionStorageForAuth === false) {
      setStorageType('localStorage');
    }
  }, [setStorageType, shouldUseSessionStorageForAuth, storageType]);

  if (loadingUserFlags || isPendingRedirection || isPendingSessionSync) {
    logger.info('loadingSpinnerHit', {
      loadingUserFlags,
      isPendingRedirection,
      isPendingSessionSync,
    });

    return <CircularProgressWrapper />;
  }

  logger.info('notLoading');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <CssBaseline />
      <ToastContainer
        // Allowing unmasking of toast text in session replays, if any toast text is found to be PII the implementation of this will need to be changed
        toastClassName="dd-privacy-allow"
        position="top-right"
        autoClose={7500}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnHover
      />
      <AlertSnackbarProvider>
        <ErrorManagementProvider initialErrors={[]}>
          <BrazeContentCardContext.Provider value={brazeContentCards}>
            {shouldShowMaintenanceScreen ? (
              <Maintenance />
            ) : (
              <PatientConsentProvider>
                <PreferencesProvider>
                  <PatientReferralProvider>
                    <AuthenticatedSnowplow>
                      <Routes />
                    </AuthenticatedSnowplow>
                  </PatientReferralProvider>
                </PreferencesProvider>
              </PatientConsentProvider>
            )}
          </BrazeContentCardContext.Provider>
        </ErrorManagementProvider>
      </AlertSnackbarProvider>
    </LocalizationProvider>
  );
}

export default App;
